/**
 * LobbyRulesCard — the lobby's compact rules card: map thumbnail, map name,
 * a wide map banner (tap, or its "Inspect ⤢" badge, → the map view), the map
 * name with "Edit game ›" (host → the edit-game screen) or "All rules ›"
 * (everyone else → the shared rules modal), and a one-line rules summary.
 *
 * Shared by the lobby and the join-by-code "found" state, so it takes the few
 * fields both a full GameResponse and the slim code preview carry.
 */

import React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import { useRouter } from 'expo-router'
import type { GameConfig } from '@warcarp/api-types'
import { MapThumbnail } from '../../map/MapThumbnail'
import { MapTag } from '../../map/MapTag'
import { isWarfishClassicsMap } from '../../map/warfish/presentation'
import { useMap } from '../../../hooks/useMaps'
import { GameRulesModal } from '../GameRulesModal'
import { effectiveLobbyConfig, lobbyRulesSummary } from '../../../lib/gameRulesSummary'

const BANNER_HEIGHT = 110

export function LobbyRulesCard({ mapId, mapName, setupConfig, playersValue, bootAfterSeconds, onEdit, maxPlayers }: {
  mapId: string | null
  mapName: string | null
  setupConfig: Partial<GameConfig> | null
  /** "Players" row of the rules modal (e.g. "2–5"). */
  playersValue: string
  bootAfterSeconds?: number
  /** Host: show "Edit game ›" and call this; otherwise "All rules ›" opens the rules modal. */
  onEdit?: () => void
  /** Seat count, for a team game's format in the summary ("3v3 · pick seats"). */
  maxPlayers?: number
}) {
  const router = useRouter()
  const [rulesOpen, setRulesOpen] = React.useState(false)
  const { data: map } = useMap(mapId ?? undefined)
  const specialRules = !!map?.specialRules?.length
  const name = mapName ?? map?.name ?? mapId ?? 'Default map'
  // Effective rules (map-fixed setups override the host's staged config); the
  // same config feeds the one-line summary and the full rules modal.
  const rulesConfig = effectiveLobbyConfig({ setupConfig }, map)
  const rulesLabel = specialRules ? 'View special game rules' : 'View game rules'
  const openMap = () => { if (mapId) router.push(`/(app)/maps/${mapId}`) }
  const pill = 'bg-paper border border-olive rounded-full px-3 items-center justify-center'

  // The banner fills the card: measure its width for the thumbnail.
  const [bannerWidth, setBannerWidth] = React.useState(0)

  return (
    <>
      <View testID="lobby-map-and-rules" className="bg-paper-up border border-olive rounded-xl p-3 gap-2.5">
        {/* Map banner: the whole preview opens the map; "Inspect ⤢" says so. */}
        {mapId ? (
          <TouchableOpacity
            testID="lobby-map-action"
            onPress={openMap}
            onLayout={(e) => setBannerWidth(Math.round(e.nativeEvent.layout.width))}
            accessibilityLabel={`Inspect map ${name}`}
            accessibilityRole="button"
            className="rounded-lg overflow-hidden border border-olive/60"
            style={{ height: BANNER_HEIGHT }}
          >
            {bannerWidth > 0 && <MapThumbnail mapId={mapId} width={bannerWidth} height={BANNER_HEIGHT} />}
            <View testID="lobby-inspect-map" pointerEvents="none" className="absolute rounded-full px-2.5 py-1"
              style={{ right: 8, bottom: 8, backgroundColor: 'rgba(0,0,0,0.65)' }}>
              <Text className="font-stencil text-[10px] uppercase tracking-wider" style={{ color: '#fff' }}>Inspect ⤢</Text>
            </View>
          </TouchableOpacity>
        ) : null}
        <View className="flex-row items-center" style={{ gap: 10 }}>
          <View style={{ flex: 1, minWidth: 0 }} className="gap-0.5">
            <Text className="text-ink font-stencil text-sm uppercase tracking-wider" numberOfLines={2}>{name}</Text>
            {isWarfishClassicsMap(map) && <MapTag variant="warfish" testID="lobby-map-warfish-tag" style={{ alignSelf: 'flex-start' }} />}
          </View>
          {onEdit ? (
            <TouchableOpacity
              testID="lobby-edit-game"
              onPress={onEdit}
              accessibilityLabel="Edit game"
              accessibilityRole="button"
              className={pill}
              style={{ minHeight: 32 }}
            >
              <Text className="text-ink font-stencil text-[11px] uppercase tracking-wider">Edit game ›</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              testID="lobby-rules-action"
              onPress={() => setRulesOpen(true)}
              accessibilityLabel={rulesLabel}
              accessibilityRole="button"
              className={pill}
              style={{ minHeight: 32 }}
            >
              <Text className="text-ink font-stencil text-[11px] uppercase tracking-wider">All rules ›</Text>
            </TouchableOpacity>
          )}
        </View>
        <Text testID="lobby-rules-summary" className="text-ink-soft font-typed text-xs" style={{ lineHeight: 17 }}>
          {specialRules ? 'Special rules · ' : ''}{lobbyRulesSummary(rulesConfig, maxPlayers)}
        </Text>
      </View>
      {!onEdit && (
        <GameRulesModal visible={rulesOpen} onClose={() => setRulesOpen(false)} config={rulesConfig}
          map={map} players={playersValue} bootAfterSeconds={bootAfterSeconds} />
      )}
    </>
  )
}
